import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { Bell, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/format';
import { retentionTone, useRetentionAlerts } from '@/features/retention/api';

/**
 * In-app alerts (FR-RET-004).
 *
 * ── What this exists to fix ─────────────────────────────────────────────────────────────
 * The customer was already emailed a week before their media expires. Nobody in the studio
 * saw it: the retention banner shows standing policy text that never changes, and the
 * dashboard tile shows a bare count over a thirty-day horizon. A customer could ring about
 * a warning that no one here could see. This puts the same event, on the same day, in front
 * of the person answering the phone.
 *
 * The window is not decided here — it comes from the server, which reads the same setting
 * the reminder emails read. See `getRetentionAlerts`.
 *
 * ── Why there is no unread state ────────────────────────────────────────────────────────
 * Deliberate. "Read" would mean a member of staff clicked a bell, which is not the same as
 * the media having been collected, and the badge would then go quiet while the deadline
 * kept running. An item leaves this list for one reason: its date passed out of the window.
 * The count is a live measure of work outstanding, not of messages seen.
 */

/** Enough to act on at a glance; the rest are one click away on the watch report. */
const VISIBLE = 5;

export function NotificationBell() {
  const navigate = useNavigate();
  const { data } = useRetentionAlerts();

  const items = Array.isArray(data?.items) ? data.items : [];
  const count = items.length;
  const windowDays = data?.windowDays ?? 7;

  const label =
    count === 0
      ? 'Alerts — nothing needs attention'
      : `Alerts — ${count} event${count === 1 ? '' : 's'} with media expiring within ${windowDays} days`;

  return (
    /*
     * `modal={false}`: Radix's default marks the rest of the page `aria-hidden` while the
     * menu is open, which leaves every focusable element in the shell hidden-but-focusable —
     * axe reports that as a serious `aria-hidden-focus` violation, and a screen-reader user
     * tabbing out of the menu lands in nothing. Non-modal also suits an alert panel better:
     * glancing at it should not freeze the page behind it.
     */
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={label}>
          <Bell className="h-5 w-5 text-slate-500" aria-hidden="true" />
          {count > 0 && (
            /*
             * aria-hidden: the number is already in the button's accessible name above.
             * Left visible here it is announced twice, once as a bare digit with no noun.
             */
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold leading-none text-white"
            >
              {count > 9 ? '9+' : count}
            </span>
          )}
        </Button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 w-[min(20rem,calc(100vw-1.5rem))] rounded-md border border-slate-200 bg-surface p-1 shadow-lg"
        >
          <div className="px-2 py-2">
            <p className="text-sm font-medium text-slate-900">Media retention</p>
            <p className="text-xs text-slate-500">
              {count === 0
                ? `Nothing expiring in the next ${windowDays} days`
                : `Expiring within ${windowDays} days`}
            </p>
          </div>

          <DropdownMenu.Separator className="my-1 h-px bg-slate-200" />

          {count === 0 ? (
            <p className="px-2 py-3 text-xs text-slate-500">
              You are up to date. Events appear here once they are inside the final warning window.
            </p>
          ) : (
            items.slice(0, VISIBLE).map((item) => {
              const { label: relative } = retentionTone(item.daysRemaining);

              return (
                <DropdownMenu.Item
                  key={item.eventId}
                  onSelect={() => navigate(`/customers/${item.customerId}`)}
                  className="cursor-pointer rounded px-2 py-2 text-sm outline-none focus:bg-slate-100 data-[highlighted]:bg-slate-100"
                >
                  <span className="flex items-start gap-2">
                    <ShieldAlert
                      className="mt-0.5 h-4 w-4 shrink-0 text-amber-600"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium text-slate-900">
                        {item.customerName}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {item.eventName ?? item.eventType} · {formatDate(item.retentionExpiryDate)}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1.5 text-xs">
                        <span className="font-medium text-rose-700">{relative}</span>
                        {/*
                         * Whether the customer has actually been told. A member of staff
                         * ringing round needs to open with the right sentence, and "we
                         * emailed you last Tuesday" is a different call from a first warning.
                         */}
                        {/* slate-500, not 400: on white, slate-400 measures 2.56:1. */}
                        <span className="text-slate-500">
                          {item.reminderCount > 0
                            ? `· ${item.reminderCount} reminder${item.reminderCount === 1 ? '' : 's'} sent`
                            : '· not yet notified'}
                        </span>
                      </span>
                    </span>
                  </span>
                </DropdownMenu.Item>
              );
            })
          )}

          {count > VISIBLE && (
            <p className="px-2 pb-1 pt-0.5 text-xs text-slate-500">and {count - VISIBLE} more</p>
          )}

          <DropdownMenu.Separator className="my-1 h-px bg-slate-200" />

          <DropdownMenu.Item
            onSelect={() => navigate('/reports/retention')}
            className="cursor-pointer rounded px-2 py-2 text-sm font-medium text-brand-700 outline-none focus:bg-slate-100 data-[highlighted]:bg-slate-100"
          >
            Open Retention Watch
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
